import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperKbdComponent } from '@ricoh-paper/angular';
import { AskMarkComponent } from '../../../../../../shared/ask/ask-mark.component';
import { LocaleService } from '../../../../../../core/services/locale.service';
import type { CmdkAskItem } from '../../models/cmdk.model';

/**
 * "Ask «query»" — a row in the results list, not a mode.
 *
 * The whole argument of the design sits in that sentence. A GUI failure is
 * bounded and self-correcting: you clicked the wrong thing, and the
 * alternatives are still on screen. A conversational failure is unbounded —
 * the system did not understand, and you must now guess a different phrasing
 * with no cue about what would work. So the results list stays the safety net,
 * and the ask is one option among them rather than a destination that replaces
 * them.
 *
 * Where the row SITS is decided by `models/cmdk-rows.ts`, not here: first when
 * nothing is a strong match, second behind an exact prefix hit (never in front
 * of a button that already works in one click), and last under
 * "Not in this filter?" when a scope chip is active.
 */
@Component({
  selector: 'im-cmdk-row-ask',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AskMarkComponent, PaperKbdComponent],
  templateUrl: './cmdk-row-ask.component.html',
  styleUrl: './cmdk-row-ask.component.scss',
})
export class CmdkRowAskComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly item = input.required<CmdkAskItem>();
  readonly selected = input(false);

  protected readonly title = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.ask.row_title', { query: this.item().query });
  });

  /**
   * Under a scope chip the row says it looks WIDER than the filter — it is the
   * way back out of a narrowing the user chose, not a competitor to it.
   */
  protected readonly subtitle = computed(() => {
    this.locale.locale();
    return this.transloco.translate(
      this.item().scopedTo ? 'shell.cmdk.ask.row_sub_scoped' : 'shell.cmdk.ask.row_sub',
    );
  });
}
